import { format, parseISO } from 'date-fns';
import { ArrowInline } from "@/lib/Icon";
import { ChevronDown, ChevronUp } from 'lucide-react';
import { useState } from 'react';

import { Button } from '@/design-system/primitives';
import { track } from '@/lib/analytics';
import { useCancelAppointment } from '@/lib/api/hooks';
import type { Appointment } from '@/types/api';
import { BookingDialog } from './BookingDialog';

/**
 * AppointmentRow — mock-matched row.
 *
 * Header band: 'VIDEO CALL' / 'PHONE CALL' / 'IN PERSON' label on the
 * left, status pill on the right (SCHEDULED / ✓ COMPLETED / CANCELLED).
 * Body: a square date tile (MMM / DD stacked) + title + 'Dr. XYZ | h:mm
 * AM'. Expandable for join link + add-to-calendar.
 */

const MODALITY_LABEL: Record<string, string> = {
  video: 'VIDEO CALL',
  phone: 'PHONE CALL',
  in_person: 'IN PERSON',
};

function statusLabel(status: string): string {
  if (status === 'scheduled') return 'SCHEDULED';
  if (status === 'completed') return '✓ COMPLETED';
  if (status === 'cancelled') return 'CANCELLED';
  return status.toUpperCase();
}

export function AppointmentRow({ appt }: { appt: Appointment }) {
  const [open, setOpen] = useState(false);
  const [rescheduleOpen, setRescheduleOpen] = useState(false);
  const [showCancelConfirm, setShowCancelConfirm] = useState(false);
  const cancel = useCancelAppointment();
  const start = parseISO(appt.start_at);
  const end = appt.end_at ? parseISO(appt.end_at) : null;
  const Caret = open ? ChevronUp : ChevronDown;
  const modality = MODALITY_LABEL[appt.modality] ?? appt.modality.toUpperCase();
  const canChange = appt.status === 'scheduled';

  return (
    <article className="border border-line-subtle bg-[var(--surface-page)]">
      <button
        type="button"
        onClick={() => setOpen((v) => !v)}
        aria-expanded={open}
        className="w-full text-left"
      >
        <div className="flex items-center justify-between px-15 py-8 border-b border-line-subtle bg-[var(--surface-sunken)]">
          <span className="type-label-sm text-ink-secondary">{modality}</span>
          <span
            className={
              'type-label-xs px-10 py-3 ' +
              (appt.status === 'scheduled'
                ? 'bg-[var(--surface-inverse)] text-[var(--ink-on-inverse)]'
                : 'text-ink-secondary')
            }
          >
            {statusLabel(appt.status)}
          </span>
        </div>
        <div className="flex items-center gap-15 px-15 py-15">
          <div className="w-50 shrink-0 bg-[var(--surface-sunken)] text-center py-5">
            <div className="type-label-xs text-ink-muted">
              {format(start, 'MMM').toUpperCase()}
            </div>
            <div className="type-title-md mt-3 leading-none">
              {format(start, 'd')}
            </div>
          </div>
          <div className="flex-1 min-w-0">
            <div className="type-title-sm capitalize">
              {appt.reason ?? appt.kind.replace(/_/g, ' ')}
            </div>
            <div className="type-body-sm text-ink-secondary mt-5">
              {appt.clinician?.display_name ?? 'Provider TBD'}
              <span className="text-ink-muted mx-5">|</span>
              {format(start, 'h:mm a')}
            </div>
          </div>
          <Caret
            size={16}
            strokeWidth={1.5}
            className="text-ink-muted shrink-0"
            aria-hidden
          />
        </div>
      </button>

      {open && (
        <div className="border-t border-line-subtle px-15 py-15 flex flex-col gap-15">
          <dl className="grid grid-cols-2 gap-10 type-body-sm">
            <div>
              <dt className="type-label-xs text-ink-muted">WHEN</dt>
              <dd className="mt-3">
                {format(start, 'EEE, MMM d · h:mm a')}
                {end ? ` – ${format(end, 'h:mm a')}` : ''}
              </dd>
            </div>
            <div>
              <dt className="type-label-xs text-ink-muted">PROVIDER</dt>
              <dd className="mt-3">
                {appt.clinician?.display_name ?? 'Provider TBD'}
              </dd>
            </div>
          </dl>
          <div className="flex items-center gap-10 flex-wrap">
            {appt.video_url && (
              <Button asChild>
                <a href={appt.video_url} target="_blank" rel="noreferrer">
                  Join video call <ArrowInline />
                </a>
              </Button>
            )}
            {appt.calendar_link && (
              <Button asChild variant="ghost" size="sm">
                <a href={appt.calendar_link} target="_blank" rel="noreferrer">
                  Add to calendar
                </a>
              </Button>
            )}
            {!appt.video_url && appt.modality === 'video' && (
              <span className="type-label-xs text-ink-muted">
                Join link will appear here closer to the appointment.
              </span>
            )}
          </div>

          {/* Reschedule / cancel — only on scheduled appointments. */}
          {canChange && !showCancelConfirm && (
            <div className="flex items-center gap-15 flex-wrap pt-5 border-t border-line-subtle">
              <button
                type="button"
                onClick={() => setRescheduleOpen(true)}
                className="type-label-sm text-ink-muted hover:text-ink underline-offset-4 hover:underline"
              >
                Reschedule
              </button>
              <button
                type="button"
                onClick={() => setShowCancelConfirm(true)}
                className="type-label-sm text-ink-muted hover:text-danger underline-offset-4 hover:underline"
              >
                Cancel appointment
              </button>
            </div>
          )}
          {showCancelConfirm && (
            <div className="border border-line-subtle px-15 py-15">
              <p className="type-body-sm">Cancel this appointment?</p>
              <div className="flex items-center gap-10 mt-15">
                <Button
                  variant="secondary"
                  size="sm"
                  onClick={() => setShowCancelConfirm(false)}
                  disabled={cancel.isPending}
                >
                  Keep
                </Button>
                <Button
                  variant="danger"
                  size="sm"
                  loading={cancel.isPending}
                  onClick={() =>
                    cancel
                      .mutateAsync(appt.id)
                      .then(() => {
                        track('appointment_cancelled', { appointment_id: appt.id });
                        setShowCancelConfirm(false);
                      })
                      .catch(() => {})
                  }
                >
                  Yes, cancel
                </Button>
              </div>
              {cancel.error && (
                <div className="type-body-sm text-danger mt-10">
                  {(cancel.error as { message?: string }).message ??
                    'Cancel failed'}
                </div>
              )}
            </div>
          )}
        </div>
      )}

      <BookingDialog
        open={rescheduleOpen}
        onOpenChange={setRescheduleOpen}
        rescheduleAppointmentId={appt.id}
      />
    </article>
  );
}
